import React, { useState, useEffect } from 'react'
import ClientForm from '../components/ClientForm'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  IdentificationIcon,
  MapPinIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { api } from '../services/authService'

const ClientsPage = () => {
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showClientForm, setShowClientForm] = useState(false)
  const [editingClient, setEditingClient] = useState(null)

  useEffect(() => {
    loadClients()
  }, [])

  const loadClients = async () => {
    setLoading(true)
    try {
      const response = await api.get('/api/clients')
      const result = response.data
      console.log('Clientes carregados da API:', result.data)
      setClients(result.data || [])
    } catch (error) {
      console.error('Erro ao carregar clientes:', error)
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.')
      } else {
        toast.error('Erro ao carregar clientes')
      }
      setClients([])
    } finally {
      setLoading(false)
    }
  }

  const filteredClients = clients.filter(client => {
    const searchLower = searchQuery.toLowerCase()
    return (
      client.name.toLowerCase().includes(searchLower) ||
      client.document.includes(searchQuery) ||
      (client.email && client.email.toLowerCase().includes(searchLower)) ||
      (client.phone && client.phone.includes(searchQuery))
    )
  })

  const handleAddClient = () => {
    setShowClientForm(true)
  }

  const handleCloseClientForm = () => {
    setShowClientForm(false)
    setEditingClient(null)
  }

  const handleClientSuccess = () => {
    loadClients()
    setShowClientForm(false)
    setEditingClient(null)
  }

  const handleViewClient = (id) => {
    const client = clients.find(c => c.id === id)
    if (client) {
      // Por enquanto, apenas mostra info. Mais tarde podemos criar modal de detalhes
      toast(`Visualizando cliente: ${client.name}`, {
      icon: 'ℹ️',
    })
    }
  }

  const handleEditClient = async (id) => {
    try {
      const response = await api.get(`/api/clients/${id}`)
      const fullClient = response.data.client || response.data
      setEditingClient(fullClient)
    } catch (error) {
      console.error('Erro ao buscar cliente para edição:', error)
      // Fallback para dados da lista
      const client = clients.find(c => c.id === id)
      setEditingClient(client)
    }
  }

  const handleDeleteClient = async (id) => {
    const client = clients.find(c => c.id === id)
    if (!confirm(`Tem certeza que deseja deletar o cliente "${client.name}"? Esta ação não pode ser desfeita.`)) {
      return
    }

    try {
      await api.delete(`/api/clients/${id}`)
      toast.success('Cliente deletado com sucesso!')
      loadClients()
    } catch (error) {
      console.error('Erro ao deletar cliente:', error)
      const message = error.response?.data?.error || 'Erro ao deletar cliente'
      toast.error(message)
    }
  }

  const handleExportClients = () => {
    toast.success('Exportando relatório de clientes...')
  }

  const formatDocument = (document) => {
    if (!document) return ''
    const numbers = document.replace(/\D/g, '')
    
    if (numbers.length === 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    } else if (numbers.length === 14) {
      return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
    }
    return document
  }

  const formatPhone = (phone) => {
    if (!phone) return ''
    const numbers = phone.replace(/\D/g, '')
    
    if (numbers.length === 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
    } else if (numbers.length === 11) {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
    }
    return phone
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const getRiskLevelLabel = (level) => {
    const labels = {
      'very_low': 'Risco Muito Baixo',
      'low': 'Risco Baixo',
      'medium': 'Risco Médio',
      'high': 'Risco Alto',
      'very_high': 'Risco Muito Alto'
    }
    return labels[level] || 'Não Classificado'
  }

  const getRiskLevelColor = (level) => {
    const colors = {
      'very_low': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-200' },
      'low': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-200' },
      'medium': { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-200' },
      'high': { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800 dark:text-orange-200' },
      'very_high': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-200' }
    }
    return colors[level] || { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-800 dark:text-gray-200' }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando clientes...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            👥 Gestão de Clientes
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gerencie sua base de clientes completa
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <button
            onClick={handleExportClients}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <DocumentArrowDownIcon className="w-4 h-4" />
            <span>Exportar</span>
          </button>
          <button
            onClick={handleAddClient}
            className="btn btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="w-4 h-4" />
            <span>Novo Cliente</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
        <div className="card p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex-shrink-0">
              <UserIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Total</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-600">{clients.length}</p>
            </div>
          </div>
        </div>

        <div className="card p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-lg bg-green-50 dark:bg-green-900/20 flex-shrink-0">
              <PhoneIcon className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Com Telefone</p>
              <p className="text-xl sm:text-2xl font-bold text-green-600">{clients.filter(c => c.phone).length}</p>
            </div>
          </div>
        </div>

        <div className="card p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex-shrink-0">
              <EnvelopeIcon className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Com Email</p>
              <p className="text-xl sm:text-2xl font-bold text-purple-600">{clients.filter(c => c.email).length}</p>
            </div>
          </div>
        </div>

        <div className="card p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 flex-shrink-0">
              <IdentificationIcon className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Pessoa Jurídica</p>
              <p className="text-xl sm:text-2xl font-bold text-yellow-600">
                {clients.filter(c => c.document && c.document.replace(/\D/g, '').length === 14).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome, documento, email ou telefone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-input pl-10"
            />
          </div>
        </div>
      </div>

      {/* Clients List - Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Documento</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">Contato</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">Cidade/UF</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden xl:table-cell">Risco</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden xl:table-cell">Cadastro</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredClients.map((client) => {
                const addr = typeof client.address === 'object' && client.address !== null ? client.address : {};
                const cityState = [addr.city, addr.state].filter(Boolean).join('/');
                const isPJ = client.document && client.document.replace(/\D/g, '').length === 14;
                const riskColor = getRiskLevelColor(client.risk_level);

                return (
                  <tr key={client.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <UserIcon className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{client.name}</div>
                          {client.email && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]">{client.email}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700 dark:text-gray-300">{formatDocument(client.document)}</span>
                        {isPJ && (
                          <span className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 px-1.5 py-0.5 rounded text-[10px] font-medium">PJ</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap hidden md:table-cell">
                      <span className="text-sm text-gray-700 dark:text-gray-300">{formatPhone(client.phone) || '-'}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                      <span className="text-sm text-gray-700 dark:text-gray-300">{cityState || '-'}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap hidden xl:table-cell">
                      {client.risk_level ? (
                        <span className={`${riskColor.bg} ${riskColor.text} px-2 py-0.5 rounded-full text-xs font-medium`}>
                          {getRiskLevelLabel(client.risk_level)}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap hidden xl:table-cell">
                      <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(client.created_at)}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => handleEditClient(client.id)}
                          className="p-1.5 text-gray-500 hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClient(client.id)}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/50 rounded-lg transition-colors"
                          title="Deletar"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {filteredClients.length === 0 && (
        <div className="text-center py-12">
          <UserIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {searchQuery ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchQuery 
              ? 'Tente ajustar sua busca ou limpar os filtros' 
              : 'Comece adicionando seu primeiro cliente'
            }
          </p>
          {!searchQuery && (
            <button onClick={handleAddClient} className="btn btn-primary">
              Adicionar Primeiro Cliente
            </button>
          )}
        </div>
      )}

      {/* Modais */}
      {showClientForm && (
        <ClientForm
          onClose={handleCloseClientForm}
          onSuccess={handleClientSuccess}
        />
      )}

      {editingClient && (
        <ClientForm
          client={editingClient}
          onClose={handleCloseClientForm}
          onSuccess={handleClientSuccess}
        />
      )}
    </div>
  )
}

export default ClientsPage